<i18n>
{
  "en": {
    "title": "2018 – 2020 Schneider Sustainability Impact",
    "description": "Formerly known as the Planet & Society Barometer, the 2018 – 2020 Schneider Sustainability Impact goals guide Schneider Electric's sustainability efforts. Renewed and updated for 2018 through 2020, this fifth iteration reflects a holistic view of sustainability and green initiatives.",
    "introduction": {
      "section": "Introduction",
      "title": "2018 – 2020 Schneider Sustainability Impact",
      "planetBarometer": {
        "vid": {
          "title": "Measuring Sustainable Progress",
          "description": "Emilienne Lepoutre, Sustainability Performance Manager, Schneider Electric"
        },
        "megatrends": {
          "title": "The five sustainability megatrends",
          "para": "In addition to the two megatrends that help us understand the global shifts in technology and energy, there are five specific megatrends that help us organize our ambition and action toward a sustainable future."
        },
        "climate": {
          "title": "Climate",
          "para": "Increases in temperatures are projected to exceed +3 – 4 °C by 2100 in a variety of scenarios according to the Intergovernmental Panel on Climate Change."
        },
        "circularEconomy": {
          "title": "Circular Economy",
          "para": "The decline in ecosystems is making natural resources scarcer and more expensive. A circular economy seeks to keep resources in use at their maximum value for as long as possible."
        },
        "ethics": {
          "title": "Ethics",
          "para": "The U.N. Ruggie Framework states that it is every company’s responsibility to respect human rights and avoid causing or contributing to adverse human rights impacts."
        },
        "development": {
          "title": "Development",
          "para": "Two of the U.N. Sustainable Development Goals are to end poverty and to ensure access to affordable, reliable, sustainable, and modern energy for all."
        },
        "healthEquity": {
          "title": "Health & Equity",
          "para": "In developing countries, human health will be increasingly affected by climate change. Other countries will face increased stress and lifestyle-based disorders."
        },
        "sustainabilityJourney": {
          "title": "A critical instrument for our sustainability journey",
          "para": "The 2018 – 2020 Schneider Sustainability Impact goals reflects the sustainability megatrends and guides Schneider’s sustainability efforts by making our goals clear. Renewed and updated for 2018 through 2020, this fifth iteration reflects our holistic view of sustainability, how our sustainability efforts affect the planet, its people, our profit, and that of our customers. It contains our promises to our partners, customers, and the world. It is the standard by which we measure ourselves and hold ourselves accountable.",
          "paraB": "The 2018 – 2020 Schneider Sustainability Impact statement is audited by a third party and progress is tracked and published quarterly at <a href='https://www.schneider-electric.com' target='_blank'>schneider-electric.com</a>."
        },
        "tableTxt": "2018 – 2020 Schneider Sustainability Impact",
        "table": {
          "row1": {
            "cell1": {
              "title": "Climate"
            },
            "cell2": {
              "p1": "<strong class=''>80%</strong> renewable electricity",
              "p2": "<strong class=''>10%</strong> CO<sub>2</sub> efficiency in transportation",
              "p3": "<strong class=''>100</strong> million metric tons of CO<sub>2</sub> avoided on our customers’ end through our offers",
              "p4": "<strong class=''>25%</strong> increase in turnover for our EcoStruxure Energy & Sustainability Services"
            }
          },
          "row2": {
            "cell1": {
              "title": "Circular Economy"
            },
            "cell2": {
              "p1": "<strong class=''>75%</strong> of sales under our new Green Premium<sup>&trade;</sup> program",
              "p2": "<strong class=''>200</strong> sites labeled toward zero waste to landfill",
              "p3": "<strong class=''>100%</strong> cardboard and pallets for transport packing from recycled or certified sources",
              "p4": "<strong class=''>100,000</strong> metric tons of avoided primary resource consumption through ecoFit<sup>&trade;</sup>, recycling, and take-back programs"
            }
          },
          "row3": {
            "cell1": {
              "title": "Ethics"
            },
            "cell2": {
              "p1": "<strong class=''>5 pts / 100 </strong>increase in average score of ISO 26000 assessment for our strategic suppliers",
              "p2": "<strong class=''>300</strong> suppliers under Human Rights & Environment vigilance received specific on-site assessment",
              "p3": "<strong class=''>100%</strong> of sales, procurement, and finance employees trained every year on anti-corruption"
            }
          },
          "row4": {
            "cell1": {
              "title": "Development"
            },
            "cell2": {
              "p1": "<strong class=''>x4</strong> turnover of our Access to Energy program",
              "p2": "<strong class=''>350,000</strong> underprivileged people trained in energy management",
              "p3": "<strong class=''>12,000</strong> volunteering days thanks to our VolunteerIn global platform"
            }
          },
          "row5": {
            "cell1": {
              "title": "Health &amp; Equity"
            },
            "cell2": {
              "p1": "<strong class=''>70%</strong> scored in our Employee Engagement Index",
              "p2": "<strong class=''>1</strong> medical incident per million hours worked",
              "p3": "<strong class=''>90%</strong> of employees have access to a comprehensive well-being at work program",
              "p4": "<strong class=''>100%</strong> of employees are working in countries that have fully deployed our Family Leave policy",
              "p5": "<strong class=''>100%</strong> of workers received 12 hours of learning in the year with 30% digital learning",
              "p6": "<strong class=''>90%</strong> of white collar workers have an individual development plan",
              "p7": "<strong class=''>95%</strong> of employees are working in a country with commitment and process in place to achieve gender pay equity"
            }
          }
        },
        "cta": "See our impact"
      }
    }
  },
  "fr": {
    "title": "2018 – 2020 Schneider Sustainability Impact",
    "description": "Formerly known as the Planet & Society Barometer, the 2018 – 2020 Schneider Sustainability Impact goals guide Schneider Electric's sustainability efforts. Renewed and updated for 2018 through 2020, this fifth iteration reflects a holistic view of sustainability and green initiatives.",
    "introduction": {
      "section": "Introduction",
      "title": "Baromètre Schneider Sustainability Impact 2018 – 2020",
      "planetBarometer": {
        "vid": {
          "title": "Mesurer des progrès durables",
          "description": "Emilienne Lepoutre, Responsable de la performance développement durable, Schneider Electric"
        },
        "megatrends": {
          "title": "Nos cinq défis clés pour le développement durable",
          "para": "Outre les deux grandes tendances qui nous aident à mieux comprendre les transformations mondiales sur le plan technologique et énergétique, cinq défis clés  nous permettent de structurer notre ambition et notre action en faveur d’un avenir durable."
        },
        "climate": {
          "title": "Climat",
          "para": "Selon les différents scénarios du Groupe d'experts intergouvernemental, sur le changement climatique, les températures devraient connaître une hausse de +3 à  4 °C  d'ici 2100."
        },
        "circularEconomy": {
          "title": "Économie circulaire",
          "para": "Le déclin des écosystèmes s'accompagne d’une raréfaction des ressources naturelles, d'un envol des prix et d'une diminution de la diversité. L'économie circulaire s’efforce d’optimiser la durée d’utilisation des ressources, d’en tirer le maximum, et de valoriser ou de réparer les produits et matériaux en fin de vie."
        },
        "ethics": {
          "title": "Éthique",
          "para": "Le rapport Ruggie établi par les Nations unies déclare que les entreprises ont le devoir de respecter les droits humains et de ne pas y porter atteinte, directement ou indirectement."
        },
        "development": {
          "title": "Développement",
          "para": "Deux des objectifs de développement durable de l’ONU visent à éliminer complètement la pauvreté dans le monde entier et à garantir l’accès de tous à des services énergétiques fiables, durables et modernes, à un coût abordable."
        },
        "healthEquity": {
          "title": "Santé & Équité",
          "para": "Dans les pays en développement, le changement climatique aura une incidence grandissante sur la santé humaine. D'autres pays feront face à des problèmes liés à l’augmentation du stress et à l’hygiène de vie."
        },
        "sustainabilityJourney": {
          "title": "Une pièce maîtresse pour notre stratégie de développement durable",
          "para": "Notre baromètre Schneider Sustainability Impact reprend ces grandes tendances et oriente les efforts de Schneider Electric en matière de développement durable par la fixation d’objectifs clairs. Revue et mise à jour pour la période 2018-2020, cette cinquième édition reflète notre vision holistique du développement durable, ainsi que la manière dont nos efforts influent sur la planète, sur ses habitants, sur nos profits et ceux de nos clients. Ce baromètre détaille nos promesses envers nos partenaires, nos clients et le monde. Il constitue la norme à partir de laquelle nous évaluons nos résultats et prenons nos responsabilités.",
          "paraB": "Le baromètre Schneider Sustainability Impact est audité par un tiers et le suivi de nos avancées est publié tous les trimestres sur <a href='https://www.schneider-electric.com' target='_blank'>schneider-electric.com</a>."
        },
        "tableTxt": "Le baromètre Schneider Sustainability Impact",
        "table": {
          "row1": {
            "cell1": {
              "title": "Climat"
            },
            "cell2": {
              "p1": "<strong class=''>80 %</strong> d'électricité renouvelable",
              "p2": "<strong class=''>10 %</strong> d’économies de CO₂ dans les transports",
              "p3": "<strong class=''>100</strong> million de tonnes de CO<sub>2</sub> évitées chez nos clients avec nos offres",
              "p4": "<strong class=''>25 %</strong> de croissance pour notre entité Energy & Sustainability Services"
            }
          },
          "row2": {
            "cell1": {
              "title": "Économie circulaire"
            },
            "cell2": {
              "p1": "<strong>75%</strong> du chiffre d'affaires dans le cadre de notre nouveau programme Green Premium",
              "p2": "<strong class=''>200</strong> sites labellisés \"Vers le zéro déchet en décharge\"",
              "p3": "<strong class=''>100 %</strong> de cartons d’emballage et de palettes pour le transport à partir de sources recyclées ou certifiées",
              "p4": "<strong class=''>100 000</strong> tonnes de consommation de ressources primaires évitées grâce au programme ecoFit<sup>TM</sup>, de recyclage et de reprise des produits"
            }
          },
          "row3": {
            "cell1": {
              "title": "Éthique"
            },
            "cell2": {
              "p1": "<strong class=''>5 pts / 100</strong> d’augmentation du score moyen de l’évaluation ISO 26000 pour nos fournisseurs stratégiques",
              "p2": "<strong class=''>300</strong> fournisseurs sous vigilance sur les droits humains et l’environnement ont reçu une évaluation dédiée sur site",
              "p3": "<strong class=''>100 %</strong> des fonctions ventes, achats et finances formées chaque année à la lutte contre la corruption"
            }
          },
          "row4": {
            "cell1": {
              "title": "Développement"
            },
            "cell2": {
              "p1": "<strong class=''>x4</strong> sur le chiffre d’affaires du programme d’Accès à l’Énergie",
              "p2": "<strong class=''>350 000</strong> personnes défavorisées formées à la gestion de l’énergie",
              "p3": "<strong class=''>12 000</strong> jours de volontariat grâce à notre plateforme globale VolunteerIn"
            }
          },
          "row5": {
            "cell1": {
              "title": "Santé & Équité"
            },
            "cell2": {
              "p1": "<strong class=''>70 %</strong> affichés par notre Employee Engagement Index",
              "p2": "<strong class=''>1</strong> incident médical par million d’heures travaillées",
              "p3": "<strong class=''>90 %</strong> des collaborateurs ont accès à un programme complet de bien-être au travail",
              "p4": "<strong class=''>100 %</strong> des collaborateurs travaillent dans des pays qui ont entièrement déployé notre politique de congé familial",
              "p5": "<strong class=''>100 %</strong> des ouvriers ont reçu 12 heures d’apprentissage dans l’année dont 30 % d'apprentissage numérique",
              "p6": "<strong class=''>90 %</strong> des cadres ont un plan de développement individuel",
              "p7": "<strong class=''>95 %</strong> des collaborateurs travaillent dans des pays avec un engagement et un processus en place pour atteindre l’équité salariale hommes/femmes"
            }
          }
        },
        "cta": "Découvrez notre impact"
      }
    }
  }
}
</i18n>

<template>
  <div id="">
    <Hero :loResSrc="require('@/assets/img/introduction/introduction-planet-barometer-hero-lowres.jpg')" :hiResSrc="require('@/assets/img/introduction/introduction-planet-barometer-hero.jpg')" chapter="01" :section="$t('introduction.section')" :title="$t('introduction.title')"/>

    <section class="m-b-120 m-t-120">
      <YouTubeVideo videoId="hBP_6ejA3Hc" :title="$t('introduction.planetBarometer.vid.title')" :description="$t('introduction.planetBarometer.vid.description')" :preview="require('@/assets/img/thumbnails/SSD_Emilienne.jpg')"/>
    </section>

    <section class="m-b-120 m-t-120 pad-120 bg-darkergrey c-palegrey">
      <Txt class="m-b-32">
        <h2 id="sustainabilitymegatrends" v-html="$t('introduction.planetBarometer.megatrends.title')"/>
        <p v-html="$t('introduction.planetBarometer.megatrends.para')"/>
      </Txt>
      <Pillar :picture="require('@/assets/img/introduction/introduction-barometer-climate.jpg')"
        :title="$t('introduction.planetBarometer.climate.title')"
        :content="$t('introduction.planetBarometer.climate.para')" />
      <Pillar :align-right="true" :picture="require('@/assets/img/introduction/GettyImages-645425977.jpg')"
        :title="$t('introduction.planetBarometer.circularEconomy.title')"
        :content="$t('introduction.planetBarometer.circularEconomy.para')"/>
      <Pillar :picture="require('@/assets/img/introduction/introduction-barometer-ethics.jpg')"
        :title="$t('introduction.planetBarometer.ethics.title')"
        :content="$t('introduction.planetBarometer.ethics.para')"/>
      <Pillar :align-right="true" :picture="require('@/assets/img/introduction/introduction-barometer-development.jpg')"
        :title="$t('introduction.planetBarometer.development.title')"
        :content="$t('introduction.planetBarometer.development.para')"/>
      <Pillar :picture="require('@/assets/img/introduction/introduction-barometer-health-equity.jpg')"
        :title="$t('introduction.planetBarometer.healthEquity.title')"
        :content="$t('introduction.planetBarometer.healthEquity.para')"/>
    </section>

    <section class="m-b-120 m-t-120">
      <Txt>
        <h2 v-html="$t('introduction.planetBarometer.sustainabilityJourney.title')"/>
        <p v-html="$t('introduction.planetBarometer.sustainabilityJourney.para')"/>
        <p v-html="$t('introduction.planetBarometer.sustainabilityJourney.paraB')"/>
      </Txt>
    </section>

    <section class="m-b-120 l-bg-white l-section-pad">
      <div class="l-content">
        <div class="row">
          <div class="col-xs-16 col-xs-offset-1 col-sm-7 col-sm-offset-2">
            <img id="schneidersustainabilityimpact" class="img-header" :src="require('@/assets/img/introduction/Schneider_Sustainability_green.png')" alt="">
          </div>
        </div>
      </div>
      <Table columns="2" class="m-b-50">
        <!-- <h2 slot="above" v-html="$t('introduction.planetBarometer.tableTxt')"></h2> -->
        <TableRow>
          <TableCell :bgimg="require('@/assets/img/introduction/_82A3652.jpg')">
            <h4 class="t-white" v-html="$t('introduction.planetBarometer.table.row1.cell1.title')"></h4>
          </TableCell>
          <TableCell>
            <p class="t-chartbody" v-html="$t('introduction.planetBarometer.table.row1.cell2.p1')"></p>
            <p class="t-chartbody" v-html="$t('introduction.planetBarometer.table.row1.cell2.p2')"></p>
            <p class="t-chartbody" v-html="$t('introduction.planetBarometer.table.row1.cell2.p3')"></p>
            <p class="t-chartbody" v-html="$t('introduction.planetBarometer.table.row1.cell2.p4')"></p>
          </TableCell>
        </TableRow>

        <TableRow>
          <TableCell :bgimg="require('@/assets/img/introduction/183031559.jpg')">
            <h4 class="t-white" v-html="$t('introduction.planetBarometer.table.row2.cell1.title')"></h4>
          </TableCell>
          <TableCell class="t-white" bgc="#3dcd58">
            <p class="t-chartbody" v-html="$t('introduction.planetBarometer.table.row2.cell2.p1')"></p>
            <p class="t-chartbody" v-html="$t('introduction.planetBarometer.table.row2.cell2.p2')"></p>
            <p class="t-chartbody" v-html="$t('introduction.planetBarometer.table.row2.cell2.p3')"></p>
            <p class="t-chartbody" v-html="$t('introduction.planetBarometer.table.row2.cell2.p4')"></p>
          </TableCell>
        </TableRow>

        <TableRow>
          <TableCell :bgimg="require('@/assets/img/introduction/Regina_Health-Equity_low_a.jpg')">
            <h4 class="t-white" v-html="$t('introduction.planetBarometer.table.row5.cell1.title')"></h4>
          </TableCell>
          <TableCell>
            <p class="t-chartbody" v-html="$t('introduction.planetBarometer.table.row5.cell2.p1')"></p>
            <p class="t-chartbody" v-html="$t('introduction.planetBarometer.table.row5.cell2.p2')"></p>
            <p class="t-chartbody" v-html="$t('introduction.planetBarometer.table.row5.cell2.p3')"></p>
            <p class="t-chartbody" v-html="$t('introduction.planetBarometer.table.row5.cell2.p4')"></p>
            <p class="t-chartbody" v-html="$t('introduction.planetBarometer.table.row5.cell2.p5')"></p>
            <p class="t-chartbody" v-html="$t('introduction.planetBarometer.table.row5.cell2.p6')"></p>
            <p class="t-chartbody" v-html="$t('introduction.planetBarometer.table.row5.cell2.p7')"></p>
          </TableCell>
        </TableRow>

        <TableRow>
          <TableCell :bgimg="require('@/assets/img/introduction/804689842.jpg')">
            <h4 class="t-white" v-html="$t('introduction.planetBarometer.table.row3.cell1.title')"></h4>
          </TableCell>
          <TableCell class="t-white" bgc="#3dcd58">
            <p class="t-chartbody" v-html="$t('introduction.planetBarometer.table.row3.cell2.p1')"></p>
            <p class="t-chartbody" v-html="$t('introduction.planetBarometer.table.row3.cell2.p2')"></p>
            <p class="t-chartbody" v-html="$t('introduction.planetBarometer.table.row3.cell2.p3')"></p>
          </TableCell>
        </TableRow>

        <TableRow>
          <TableCell :bgimg="require('@/assets/img/introduction/A2E_Myanmar_2017SSD.jpg')">
            <h4 class="t-white" v-html="$t('introduction.planetBarometer.table.row4.cell1.title')"></h4>
          </TableCell>
          <TableCell>
            <p class="t-chartbody" v-html="$t('introduction.planetBarometer.table.row4.cell2.p1')"></p>
            <p class="t-chartbody" v-html="$t('introduction.planetBarometer.table.row4.cell2.p2')"></p>
            <p class="t-chartbody" v-html="$t('introduction.planetBarometer.table.row4.cell2.p3')"></p>
          </TableCell>
        </TableRow>
      </Table>

      <div class="l-content">
        <div class="row">
          <div class="col-xs-16 col-xs-offset-1 col-sm-14 col-sm-offset-2">
            <CTA href="/static/2018-2020_Schneider_Sustainability_Impact_final.pdf" :buttonText="$t('introduction.planetBarometer.cta')" target="_blank"/>
          </div>
        </div>
      </div>
    </section>

    <div class="l-content">
      <FooterNav
        nextRoute="Introduction_EnvironmentalStrategy"
        prevRoute="Introduction_SustainabilityAtSchneiderElectric" />
    </div>
  </div>
</template>

<script>
import FooterNav from '@/components/FooterNav'
import Hero from '@/components/Hero'
import YouTubeVideo from '@/components/YouTubeVideo'
import Txt from '@/components/Txt'
import Pillar from '@/components/Pillar'
import Table from '@/components/Table'
import TableRow from '@/components/TableRow'
import TableCell from '@/components/TableCell'
import CTA from '@/components/CTA'
import ImageFull from '@/components/ImageFull'

export default {
  name: 'Introduction_WorkIndex',
  metaInfo () {
    return {
      title: this.$t('title'),
      meta: [
        { name: 'description', content: this.$t('description') }
      ]
    }
  },
  mounted () {
    if (window.location.hash.substr(1).length > 0) {
      this.$nextTick(function () {
        document.getElementById(window.location.hash.substr(1)).scrollIntoView()
      })
    }
  },
  components: {
    FooterNav,
    Hero,
    YouTubeVideo,
    Txt,
    Pillar,
    Table,
    TableRow,
    TableCell,
    CTA,
    ImageFull
  }
}
</script>

<style>
.img-header {
  width: 100%;
}
</style>



// WEBPACK FOOTER //
// src/pages/introduction/the-schneider-sustainability-works-index.vue